import React, { ChangeEvent, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import * as Yup from 'yup';
import {
  Box,
  Container,
  Paper,
  Typography,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  OutlinedInput,
  FormHelperText,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  IconButton,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { COLORS } from '../../theme/themeConstants';
import 'react-quill-new/dist/quill.snow.css';
import MyEditor from './RichTextEditor';
import MarkdownEditor from './MarkdownEditor';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '../../theme';
import PeakHoursComponent from '../Statefull/Settings/GeneralSettings/PeakHoursComponent';
interface FormField {
  value?: any;
  name: string;
  label?: string | ((values: any) => string);
  type?: string;
  options?: { value: string; label: string }[];
  radioOptions?: string[];
  checkOptions?: string[];
  isRequired?: boolean;
  tooltip?: string;
  selectWidth?: any;
  selectLength?: boolean;
  dependsOn?: {
    field: string;
    value: string;
  };
  suffix?: {
    type: string;
    options: string[];
  };
  component?: (props: { field: any; form: any }) => React.ReactElement | null;
  sectionTitle?: string;
  sectionLine?: boolean;
  disabled?: boolean | ((values: any) => boolean);
  sx?: ((values: any) => any) | any;
  relatedFieldToClearError?: string;
  placeholder?: string | ((values: any) => string);
}

type OptionType = {
  label: string;
  value: string | number;
};

interface FormConfig {
  delivery?: string;
  title?: string;
  initialValues: Record<string, any>;
  validationSchema: Yup.ObjectSchema<any>;
  onSubmit: (values: Record<string, any>) => void;
  fields: (FormField & {
    disabled?: boolean | ((values: any) => boolean);
    sx?: ((values: any) => any) | any;
  })[];
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  enableReinitialize?: boolean;
  // customButton?: React.ReactNode;
  customButton?: React.ReactNode | ((formikProps: any) => React.ReactNode);
  validationContext?: Record<string, any>;
}

const allForm = (config: FormConfig) => {
  return () => {
    const [showPasswordMap, setShowPasswordMap] = useState<
      Record<string, boolean>
    >({});

    const handleClickShowPassword = (name: string) => {
      setShowPasswordMap((prevMap) => ({
        ...prevMap,
        [name]: !prevMap[name],
      }));
    };

    const handleMouseDownPassword = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault();
    };

    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="lg" sx={{ py: 2 }}>
          {/* Form Container */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mt: 1,
              bgcolor: '#fff',
              borderRadius: 1,
              border: 'none',
              maxWidth: 1040,
              mx: 'auto',
            }}
          >
            <Formik
              initialValues={config.initialValues}
              validationSchema={config.validationSchema}
              onSubmit={async (values, formikBag) => {
                const errors = await formikBag.validateForm();
                if (Object.keys(errors).length > 0) {
                  formikBag.setTouched(
                    Object.keys(errors).reduce((acc, key) => {
                      acc[key] = true;
                      return acc;
                    }, {} as Record<string, boolean>)
                  );
                  return;
                }
                config.onSubmit(values);
              }}
              validateOnChange={config.validateOnChange}
              validateOnBlur={config.validateOnBlur}
              enableReinitialize={config.enableReinitialize}
              validateOnMount={false}
              context={config.validationContext}
            >
              {({
                values,
                setFieldValue,
                setFieldError,
                setFieldTouched,
                setTouched,
                errors,
                touched,
                resetForm,
                handleSubmit,
                validateForm,
                isSubmitting,
              }) => (
                <Form>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      columnGap: 4,
                      rowGap: 2,
                    }}
                  >
                    {config.fields.map(
                    ({
                      name,
                      label,
                      type = 'text',
                      options,
                      radioOptions,
                      checkOptions,
                      isRequired,
                      dependsOn,
                      suffix,
                      selectWidth,
                      component,
                      sectionTitle,
                      sectionLine,
                      disabled = false,
                      sx,
                      placeholder,
                    }) => {
                      if (type === 'section') {
                        return (
                          <Box key={sectionTitle} sx={{ mb: 2, mt: 3 }}>
                            {sectionTitle && (
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: 20,
                                  marginBottom: 8,
                                  fontFamily: 'Livvic',
                                }}
                              >
                                {sectionTitle}
                              </div>
                            )}
                            {sectionLine && <hr style={{ marginBottom: 16 }} />}
                          </Box>
                        );
                      }
                      if (
                        dependsOn &&
                        values[dependsOn.field] !== dependsOn.value
                      ) {
                        return null;
                      }

                      const fieldSx = typeof sx === 'function' ? sx(values) : sx || {};
                      return (
                        <Box
                          key={name}
                          sx={{
                            mb: 3,
                            ...fieldSx,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: { xs: 1, sm: 0 },
                              justifyContent: 'center',
                            }}
                          >
                            <FormLabel
                              htmlFor={name}
                              required={isRequired}
                              sx={{
                                width: label
                                  ? { xs: '100%', sm: '140px' }
                                  : '0',
                                color: COLORS.text.primary,
                                fontSize: 14,
                                fontWeight: 500,
                                textAlign: { xs: 'left', sm: 'right' },
                                pr: { xs: 0, sm: 2 },
                                pt: { xs: 0, sm: 1 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: {
                                  xs: 'flex-start',
                                  sm: 'flex-end',
                                },
                                '& .MuiFormLabel-asterisk': {
                                  color: 'red',
                                },
                              }}
                            >
                              {typeof label === 'function' ? label(values) : label}
                            </FormLabel>
                            <Box
                              sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                ml: label ? 0 : '89px',
                              }}
                            >
                              {component ? (
                                <>
                                  <Field name={name}>
                                    {({ field, form }: any) =>
                                      component({ field, form })
                                    }
                                  </Field>
                                  {/* Show error message for customWithError components that need Form error display */}
                                  {type === 'customWithError' && !disabled && touched[name] && errors[name] && (
                                    <FormHelperText
                                      error
                                      sx={{
                                        mt: 0,
                                        fontSize: 12,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {typeof errors[name] === 'string' ? errors[name] : 'Invalid input'}
                                    </FormHelperText>
                                  )}
                                </>
                              ) : type === 'password' ||
                                type === 'passwordVisibility' ? (
                                <FormControl>
                                  <TextField
                                    className="input-large"
                                    name={name}
                                    type={
                                      showPasswordMap[name]
                                        ? 'text'
                                        : 'password'
                                    }
                                    value={values[name]}
                                    onChange={(e) =>
                                      setFieldValue(name, e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === ' ') {
                                        e.preventDefault();
                                      }
                                    }}
                                    onBlur={() => setFieldTouched(name, true)}
                                    error={Boolean(
                                      touched[name] && errors[name]
                                    )}
                                    placeholder={
                                      typeof placeholder === 'function' 
                                        ? placeholder(values)
                                        : label === 'Confirm Password:'
                                        ? 'Enter Password Again'
                                        : `Enter ${(typeof label === 'function' ? label(values) : label)?.replace(/:$/, '') || ''}`
                                    }
                                    disabled={typeof disabled === 'function' ? disabled(values) : disabled}
                                    sx={{
                                      mb: 0,
                                      width: '400px',
                                      '& .MuiInputBase-root': {
                                        fontSize: 14,
                                        color: 'text.primary',
                                        width: '100%',
                                        height: '40px',
                                      },
                                      '& .MuiFormLabel-root': {
                                        fontSize: 14,
                                      },
                                      ...(typeof sx === 'function' ? sx(values) : sx || {}),
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            onClick={() =>
                                              handleClickShowPassword(name)
                                            }
                                            onMouseDown={
                                              handleMouseDownPassword
                                            }
                                            edge="end"
                                          >
                                            {showPasswordMap[name] ? (
                                              <Visibility />
                                            ) : (
                                              <VisibilityOff />
                                            )}
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </FormControl>
                              ) : type === 'startTime' || type === 'endTime' ? (
                                <FormControl fullWidth>
                                  <TextField
                                    className="input-large"
                                    name={name}
                                    type="time"
                                    value={values[name]}
                                    onChange={(e) =>
                                      setFieldValue(name, e.target.value)
                                    }
                                    onBlur={() => setFieldTouched(name, true)}
                                    error={Boolean(
                                      touched[name] && errors[name]
                                    )}
                                    placeholder={`Select ${(typeof label === 'function' ? label(values) : label)?.replace(/:$/, '')}`}
                                    disabled={typeof disabled === 'function' ? disabled(values) : disabled}
                                    sx={{
                                      mb: 2,
                                      '& .MuiInputBase-root': {
                                        fontSize: 14,
                                        color: 'text.primary',
                                        width: '100%',
                                        height: '40px',
                                      },
                                      '& .MuiFormLabel-root': {
                                        fontSize: 14,
                                      },
                                      ...(typeof sx === 'function' ? sx(values) : sx || {}),
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <AccessTimeIcon fontSize="small" />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </FormControl>
                              ) : type === 'number' ? (
                                <FormControl>
                                  <TextField
                                    className="input-large"
                                    name={name}
                                    type="text" // Use text for custom filtering
                                    value={values[name] ?? ''}
                                    onChange={(e) => {
                                      const inputValue = e.target.value;
                                      // Allow digits and a single decimal point
                                      const cleaned = inputValue.replace(/[^0-9.]/g, '');
                                      const parts = cleaned.split('.');
                                      const normalized =
                                        parts.length > 1
                                          ? `${parts.shift()}.${parts.join('')}`
                                          : cleaned;
                                      setFieldValue(name, normalized);
                                      setFieldTouched(name, true, false);
                                      setTimeout(() => {
                                        if (touched[name] && errors[name]) {
                                          setFieldError(name, undefined);
                                        }
                                      }, 0);
                                    }}
                                    onBlur={() => setFieldTouched(name, true)}
                                    error={Boolean(
                                      touched[name] && errors[name]
                                    )}
                                    placeholder={`Enter ${(typeof label === 'function' ? label(values) : label)?.replace(/:$/, '') || ''}`}
                                    disabled={typeof disabled === 'function' ? disabled(values) : disabled}
                                    inputProps={{
                                      inputMode: 'decimal',
                                      pattern: '^\\d*\\.?\\d*$',
                                    }}
                                    sx={{
                                      mb: 0,
                                      width: '400px',
                                      '& .MuiInputBase-root': {
                                        fontSize: 14,
                                        color: 'text.primary',
                                        width: '100%',
                                        height: '40px',
                                      },
                                      '& .MuiFormLabel-root': {
                                        fontSize: 14,
                                      },
                                      ...(typeof sx === 'function' ? sx(values) : sx || {}),
                                    }}
                                  />
                                </FormControl>
                              ) : type === 'mobile' ? (
                                <FormControl
                                  fullWidth
                                  error={touched[name] && Boolean(errors[name])}
                                  sx={{
                                    width: '100%',
                                    '& .react-tel-input': {
                                      width: '100%',
                                    },
                                    '& .form-control': {
                                      width: '100% !important',
                                      height: '40px !important',
                                      paddingLeft: '48px !important',
                                      boxSizing: 'border-box',
                                      fontSize: '14px',
                                      borderRadius: '4px',
                                      color: 'rgba(0, 0, 0, 0.87)',
                                    },
                                    '& .flag-dropdown': {
                                      border: 'none',
                                      background: 'transparent',
                                      height: '40px',
                                      borderRadius: '4px 0 0 4px',
                                      borderRight: 'none',
                                      '&.open': {
                                        backgroundColor: 'transparent',
                                      },
                                    },
                                    '& .selected-flag': {
                                      height: '40px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      backgroundColor: 'transparent !important',
                                      '&:hover': {
                                        backgroundColor:
                                          'transparent !important',
                                      },
                                    },
                                    '& .country-list': {
                                      marginTop: '4px',
                                    },
                                    ...(typeof sx === 'function' ? sx(values) : sx || {}),
                                  }}
                                >
                                  <PhoneInput
                                    country="pk"
                                    onlyCountries={[
                                      'jo',
                                      'sa',
                                      'ae',
                                      'kw',
                                      'qa',
                                      'bh',
                                      'om',
                                      'pk',
                                    ]}
                                    value={values[name]}
                                    disabled={typeof disabled === 'function' ? disabled(values) : disabled}
                                    onChange={(
                                      value: string,
                                      data: CountryData | object
                                    ) => {
                                      const country = data as CountryData;
                                      const dialCode = country?.dialCode || '';

                                      // Remove dial code from start
                                      let rawNumber = value.startsWith(
                                        `+${dialCode}`
                                      )
                                        ? value.slice(dialCode.length + 1)
                                        : value;

                                      rawNumber = rawNumber.replace(/\D/g, '');
                                      setFieldValue(name, rawNumber);
                                    }}
                                    onBlur={() => setFieldTouched(name, true)}
                                    specialLabel=""
                                    placeholder="Enter Mobile No"
                                    inputClass="input-large"
                                    inputProps={{
                                      name,
                                      required: true,
                                      autoFocus: false,
                                      style: {
                                        width: '100%',
                                        height: '40px',
                                        fontSize: '14px',
                                        paddingLeft: '48px',
                                        borderRadius: '4px',
                                        border:
                                          touched[name] && Boolean(errors[name])
                                            ? '1px solid #d32f2f'
                                            : '1px solid #ccc',
                                        backgroundColor: '#fff',
                                      },
                                    }}
                                    containerStyle={{ width: '100%' }}
                                    buttonStyle={{
                                      border: 'none',
                                      background: 'transparent',
                                      paddingLeft: '10px',
                                      height: '40px',
                                      borderRadius: '4px 0 0 4px',
                                    }}
                                  />
                                </FormControl>
                              ) : type === 'select' || options ? (
                                <FormControl
                                  sx={{ width: selectWidth || '100%' }}
                                >
                                  <Select
                                    id={name}
                                    name={name}
                                    value={values[name]}
                                    onBlur={() => setFieldTouched(name, true)}
                                    onChange={(e: SelectChangeEvent<string>) => {
                                      setFieldValue(name, e.target.value);
                                      setFieldTouched(name, true, false);
                                      // Immediately validate and clear errors if valid
                                      if (touched[name] && errors[name] && e.target.value) {
                                        setFieldError(name, undefined);
                                      }
                                    }}
                                    displayEmpty
                                    disabled={typeof disabled === 'function' ? disabled(values) : disabled}
                                    input={
                                      config.fields.find((f) => f.name === name)
                                        ?.selectLength ? (
                                        <OutlinedInput
                                          className="input-large"
                                          sx={{ height: '40px', width: '100%' }}
                                        />
                                      ) : (
                                        <OutlinedInput
                                          notched={false}
                                          sx={{
                                            height: '40px',
                                            fontWeight: '500',
                                          }}
                                        />
                                      )
                                    }
                                    sx={{
                                      '& .MuiSelect-select': {
                                        color: 'rgba(0, 0, 0, 0.6)',
                                      },
                                      ...(typeof sx === 'function' ? sx(values) : sx || {}),
                                    }}
                                  >
                                    {/* <MenuItem value="">-- Select --</MenuItem> */}
                                    <MenuItem value="" disabled>
                                      <span
                                        style={{ color: 'rgba(0, 0, 0, 0.6)' }}
                                      >
                                        -- Select --
                                      </span>
                                    </MenuItem>
                                    {(options || []).map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                        sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : type === 'radio' && radioOptions ? (
                                <FormControl>
                                  <RadioGroup
                                    row
                                    name={name}
                                    value={values[name]}
                                    onBlur={() => setFieldTouched(name, true)}
                                    onChange={(
                                      e: ChangeEvent<HTMLInputElement>
                                    ) => {
                                      setFieldValue(name, e.target.value);
                                      setFieldTouched(name, true, false);
                                      // Immediately validate and clear errors if valid
                                      if (touched[name] && errors[name] && e.target.value) {
                                        setFieldError(name, undefined);
                                      }
                                    }}
                                    sx={{ 
                                      gap: 2.5, 
                                      mt: 0.5,
                                      ...(typeof sx === 'function' ? sx(values) : sx || {}),
                                    }}
                                  >
                                    {radioOptions.map((option) => (
                                      <FormControlLabel
                                        key={option}
                                        value={option}
                                        control={
                                          <Radio
                                            size="small"
                                            disabled={typeof disabled === 'function' ? disabled(values) : disabled}
                                            sx={{
                                              color: 'text.secondary',
                                              '&.Mui-checked': {
                                                color: 'primary.main',
                                              },
                                            }}
                                          />
                                        }
                                        label={
                                          <Typography
                                            sx={{
                                              fontSize: 14,
                                              color: COLORS.text.secondary,
                                            }}
                                          >
                                            {option}
                                          </Typography>
                                        }
                                        sx={{
                                          margin: 0,
                                          '& .MuiFormControlLabel-label': {
                                            fontSize: 14,
                                            color: 'text.secondary',
                                          },
                                        }}
                                      />
                                    ))}
                                  </RadioGroup>
                                </FormControl>
                              ) : type === 'checkbox' && checkOptions ? (
                                <FormControl>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2.5,
                                      mt: 0.5,
                                      ...(typeof sx === 'function' ? sx(values) : sx || {}),
                                    }}
                                  >
                                    {checkOptions.map((option) => (
                                      <FormControlLabel
                                        key={option}
                                        control={
                                          <Checkbox
                                            size="small"
                                            disabled={typeof disabled === 'function' ? disabled(values) : disabled}
                                            checked={
                                              Array.isArray(values[name])
                                                ? values[name].includes(option)
                                                : false
                                            }
                                            onBlur={() =>
                                              setFieldTouched(name, true)
                                            }
                                            onChange={(
                                              e: ChangeEvent<HTMLInputElement>
                                            ) => {
                                              const currentValues =
                                                Array.isArray(values[name])
                                                  ? values[name]
                                                  : [];
                                              if (e.target.checked) {
                                                setFieldValue(name, [
                                                  ...currentValues,
                                                  option,
                                                ]);
                                              } else {
                                                setFieldValue(
                                                  name,
                                                  currentValues.filter(
                                                    (v: string) => v !== option
                                                  )
                                                );
                                              }
                                            }}
                                            sx={{
                                              color: 'text.secondary', // Default color when unchecked
                                              '&.Mui-checked': {
                                                color: 'primary.main', // Blue color when checked
                                              },
                                            }}
                                          />
                                        }
                                        label={
                                          <Typography
                                            sx={{
                                              fontSize: 14,
                                              color: 'text.secondary',
                                            }}
                                          >
                                            {option}
                                          </Typography>
                                        }
                                        sx={{
                                          margin: 0,
                                          '& .MuiFormControlLabel-label': {
                                            fontSize: 14,
                                            color: 'text.secondary',
                                          },
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </FormControl>
                              ) : type === 'richtext' ? (
                                <FormControl
                                  fullWidth
                                  sx={{ mt: 2 }}
                                  error={touched[name] && Boolean(errors[name])}
                                >
                                  <Box
                                    sx={{
                                      border: '1px solid #ddd',
                                      borderRadius: '4px',
                                      overflow: 'hidden',
                                      '.ql-container': {
                                        border: 'none',
                                        fontFamily: 'inherit',
                                      },
                                      '.ql-toolbar': {
                                        borderBottom: '1px solid #ddd',
                                      },
                                      '.ql-editor': {
                                        minHeight: '150px',
                                        padding: '12px',
                                        whiteSpace: 'pre-wrap',
                                        wordWrap: 'break-word',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word', maxWidth: '476px', // ✅ limit width
                                        boxSizing: 'border-box',
                                      },
                                      ...(typeof sx === 'function' ? sx(values) : sx || {}),
                                    }}
                                  >
                                    <MyEditor
                                      value={values[name]}
                                      onChange={(val) =>
                                        setFieldValue(name, val)
                                      }
                                      onBlur={() =>
                                        setFieldTouched(name, true, true)
                                      }
                                      // disabled={typeof disabled === 'function' ? disabled(values) : disabled}
                                    />
                                  </Box>
                                  {/* {touched[name] && errors[name] && (
                                    <FormHelperText sx={{ mt: 0.5 }}>
                                      {typeof errors[name] === 'object'
                                        ? 'Please check the input'
                                        : errors[name]}
                                    </FormHelperText>
                                  )} */}
                                </FormControl>
                              ) : type === 'markdown' ? (
                                <FormControl
                                  fullWidth
                                  sx={{ mt: 2, width: '100%', maxWidth: '100%' }}
                                  error={touched[name] && Boolean(errors[name])}
                                >
                                  <Box sx={{ width: '100%', maxWidth: '100%' }}>
                                    <MarkdownEditor
                                      value={values[name]}
                                      onChange={(val) => {
                                        setFieldValue(name, val);
                                        setFieldTouched(name, true, false);
                                      }}
                                      onBlur={() => setFieldTouched(name, true)}
                                      placeholder={`Enter ${(typeof label === 'function' ? label(values) : label)?.replace(/:$/, '') || ''}`}
                                      label={typeof label === 'function' ? label(values) : label}
                                      error={touched[name] && Boolean(errors[name])}
                                    />
                                  </Box>
                                </FormControl>
                              ) : type === 'text' ? (
                                <FormControl>
                                  <TextField
                                    className="input-large"
                                    name={name}
                                    value={values[name]}
                                    onChange={(e) => {
                                      setFieldValue(name, e.target.value);
                                      setFieldTouched(name, true, false);
                                      // Immediately validate and clear errors if valid
                                      if (touched[name] && errors[name]) {
                                        // Use a more immediate approach
                                        const newValue = e.target.value;
                                        if (newValue && newValue.trim()) {
                                          setFieldError(name, undefined);
                                        }
                                      }
                                    }}
                                    onBlur={() => setFieldTouched(name, true)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault(); // ✅ Prevent form submit
                                      }
                                    }}
                                    error={Boolean(
                                      touched[name] && errors[name]
                                    )}
                                    placeholder={`Enter ${(typeof label === 'function' ? label(values) : label)?.replace(/:$/, '') || ''}`}
                                    disabled={typeof disabled === 'function' ? disabled(values) : disabled}
                                    sx={{
                                      mb: 0,
                                      width: '400px',
                                      '& .MuiInputBase-root': {
                                        fontSize: 14,
                                        color: 'text.primary',
                                        width: '100%',
                                        height: '40px',
                                      },
                                      '& .MuiFormLabel-root': {
                                        fontSize: 14,
                                      },
                                      ...(typeof sx === 'function' ? sx(values) : sx || {}),
                                    }}
                                  />
                                </FormControl>
                              ) : type === 'peakHours' ? (
                                <PeakHoursComponent
                                  value={values[name]}
                                  onChange={(value) => {
                                    setFieldValue(name, value);
                                    setFieldTouched(name, true);
                                  }}
                                  error={touched[name] && errors[name] ? (typeof errors[name] === 'string' ? errors[name] : '') : undefined}
                                  touched={Boolean(touched[name])}
                                />
                              ) : type === 'customSelect' && component ? (
                                <Field name={name} key={name}>
                                  {({ field, form }: any) => (
                                    <>
                                      {React.createElement(component, {
                                        field,
                                        form,
                                        value: field.value, // optional, component can read from field.value
                                        onChange: (value: any) =>
                                          form.setFieldValue(name, value),
                                      })}
                                      {/* Don't show error message for customSelect components as they handle their own errors */}
                                    </>
                                  )}
                                </Field>
                              ) : type === 'selectsearch' && options ? (
                                <FormControl fullWidth>
                                  <Autocomplete
                                    options={options}
                                    getOptionLabel={(option) => option.label}
                                    value={
                                      (options as OptionType[])?.find(
                                        (opt) => opt.value === values[name]
                                      ) || null
                                    }
                                    onChange={(_, newValue) => {
                                      setFieldValue(
                                        name,
                                        newValue ? newValue.value : ''
                                      );
                                      setFieldTouched(name, true, false);
                                      // Immediately validate and clear errors if valid
                                      if (touched[name] && errors[name] && newValue) {
                                        setFieldError(name, undefined);
                                      }
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        // label={label}
                                        error={Boolean(
                                          touched[name] && errors[name]
                                        )}
                                        placeholder={`Select ${(typeof label === 'function' ? label(values) : label)?.replace(/^Select /i, '').replace(/:$/, '') || ''}`}
                                        sx={{
                                          mb: 0,
                                          '& .MuiInputBase-root': {
                                            fontSize: 14,
                                            color: 'text.primary',
                                            width: '100%',
                                            height: '40px',
                                          },
                                          '& .MuiFormLabel-root': {
                                            fontSize: 14,
                                          },
                                        }}
                                      />
                                    )}
                                  />
                                </FormControl>
                              ) : (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <OutlinedInput
                                    id={name}
                                    name={name}
                                    type={type}
                                    value={values[name]}
                                    onChange={(
                                      e: ChangeEvent<HTMLInputElement>
                                    ) => setFieldValue(name, e.target.value)}
                                    fullWidth
                                    sx={{
                                      height: '40px',
                                      width: suffix
                                        ? 'calc(100% - 100px)'
                                        : '100%',
                                    }}
                                  />
                                  {suffix && (
                                    <Select
                                      value={values[`${name}Unit`]}
                                      onChange={(
                                        e: SelectChangeEvent<string>
                                      ) =>
                                        setFieldValue(
                                          `${name}Unit`,
                                          e.target.value
                                        )
                                      }
                                      input={
                                        <OutlinedInput
                                          sx={{
                                            height: '40px',
                                            ml: 1,
                                            width: '90px',
                                          }}
                                        />
                                      }
                                    >
                                      {suffix.options.map((option) => (
                                        <MenuItem key={option} value={option}>
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </Box>
                              )}

                              {/* Show error message for non-custom type fields */}
                              {type !== 'custom' && type !== 'nested' && type !== 'customSelect' && type !== 'customWithError' && type !== 'peakHours' && !(typeof disabled === 'function' ? disabled(values) : disabled) && (
                                <ErrorMessage name={name}>
                                  {(msg) => (
                                    <FormHelperText
                                      error
                                      sx={{
                                        mt: 0,
                                        fontSize: 12,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {msg}
                                    </FormHelperText>
                                  )}
                                </ErrorMessage>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                  )}
                  </Box>
                  <Box
                    sx={{
                      mt: 3,
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    {typeof config.customButton === 'function'
                      ? config.customButton({
                        values,
                        setFieldValue,
                        setFieldTouched,
                          setTouched,
                        errors,
                        touched,
                        resetForm,
                        handleSubmit,
                        validateForm,
                          isSubmitting,
                      })
                      : config.customButton}
                  </Box>
                </Form>
              )}
            </Formik>
          </Paper>
        </Container>
      </ThemeProvider>
    );
  };
};

export default allForm;